import type { DropdownMenuItem } from '@timelyapp/react';
import { Button, ButtonRow, DropdownMenu, PopoverConfirm } from '@timelyapp/react';
import classNames from 'classnames';
import EmojiPickerDropdown from 'components/EmojiPickerDropdown';
import { Ellipsis } from 'components/Icons';
import Input from 'components/Input';
import InputDecorator from 'components/InputDecorator';
import { TicMetaIcon } from 'components/TicMetaIcon';
import { errorMessageFactory } from 'factories/api';
import { debounce } from 'lodash-es';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useDrag, useDrop } from 'react-dnd';
import FontAwesome from 'react-fontawesome';
import { connect } from 'react-redux';
import { getXhrStatus, makeGetTagXhrStatusStateByKey } from 'redux/modules/tags.selectors';
import type { TagDraft, TagNormalized } from 'redux/modules/tags.types';
import type { UserPermissionsFacade } from 'redux/modules/user.types';
import type { IState } from 'redux/state';
import type { Tag } from 'types/tag';
import { capitalize, ucFirst } from 'utils/text-helper';
import styles from './TagsEditor.module.scss';

interface OwnProps {
  createMode: boolean;
  onCancelDraft: () => void;
  fetchTags: () => void;
  setTagActionDetails: (tag: Tag | TagDraft | null) => void;
  onToggleArchive: (tagId: number) => void;
  onInitializeTagDraft: (props: { parent_id: number }) => void;
  onSave: (tag: Tag | TagDraft, clearDraft: boolean) => Promise<TagNormalized>;
  onSort: (options: { source: Tag | TagDraft; target: Tag | TagDraft; save?: boolean }) => void;
  tag: Tag | TagDraft;
  children?: React.ReactElement | Array<React.ReactElement>;
  error?: string;
  permissions: UserPermissionsFacade;
  isDeleting?: boolean;
}

interface State {
  name: string;
  emoji?: string;
  error?: any;
}

const makeMapStateToProps = () => {
  const getTagXhrStatusStateByKey = makeGetTagXhrStatusStateByKey();
  const mapStateToProps = (state: IState, ownProps: OwnProps) => {
    const key = ownProps.tag.id;
    const generalXhrStatus = getXhrStatus(state);
    const xhrStatus = key ? getTagXhrStatusStateByKey(state, { key: key.toString() }) : null;

    const deletingXhr = key
      ? getTagXhrStatusStateByKey(state, { key: `${key.toString()}-deleting` })
      : null;
    const isSaving = generalXhrStatus.isSaving;
    return { xhrStatus, isDeleting: !!deletingXhr?.isSaving, isSaving };
  };
  return mapStateToProps;
};

type StateProps = ReturnType<ReturnType<typeof makeMapStateToProps>>;

type Props = OwnProps & StateProps;

const TAG_EDITOR_ITEM_TYPE = 'tagEditorItem';

const TagsEditorItem: React.FC<Props> = props => {
  const { tag, children, permissions, isDeleting, onSort, fetchTags, setTagActionDetails } = props;

  const [editorState, setTagEditorState] = useState<State>({
    name: tag.name || '',
    emoji: tag.emoji,
    error: null,
  });

  const tagRef = useRef<HTMLInputElement>(null);
  const dragRef = useRef<HTMLDivElement>(null);

  const handleSort = useCallback(
    (source: Tag | TagDraft, target: Tag | TagDraft, save?: boolean) => {
      onSort({ source, target, save });
    },
    [onSort]
  );

  const [{ isDragging }, drag] = useDrag(() => ({
    type: TAG_EDITOR_ITEM_TYPE,
    item: () => ({ tag }),
    collect: monitor => ({ isDragging: monitor.isDragging() }),
    canDrag: () => !!tag.active,
    end: (_, monitor) => {
      if (!monitor.didDrop()) {
        fetchTags();
      }
    },
  }));

  const [, drop] = useDrop({
    accept: TAG_EDITOR_ITEM_TYPE,
    collect: monitor => ({
      handlerId: monitor.getHandlerId(),
      isOver: monitor.canDrop() && monitor.isOver(),
    }),
    hover: debounce((item: OwnProps) => {
      const tagCopy = { ...item.tag };
      const dragIndex = item.tag.sequence;
      const hoverIndex = tag.sequence;

      if (dragIndex === hoverIndex) return;

      handleSort(tagCopy, tag, false);
      item.tag.sequence = hoverIndex;
    }, 50),
    canDrop: item => {
      return item.tag.parent_id === tag.parent_id;
    },
    drop: (item: OwnProps) => {
      handleSort(item.tag, tag, true);
    },
  });

  function archiveConfirmationText() {
    let typeString = 'the tag';
    if (!props.tag.parent_id) {
      typeString = 'the tag list';
    }

    return `This will archive ${typeString} "${props.tag.name}" on this workspace. You can restore it later.`;
  }

  const handleArchive = () => {
    if (props.tag.id) {
      props.onToggleArchive(props.tag.id);
    }
  };

  const handleChangeName = (event: React.ChangeEvent<HTMLInputElement>) => {
    setTagEditorState({ ...editorState, name: event.target.value, error: null });
  };

  const handleInputBlur = () => {
    if (!props.createMode) {
      handleSubmit();
    }
  };

  const handleInputKeyDown = (event: React.KeyboardEvent) => {
    const key = event.key.toLowerCase();
    if (key === 'enter') {
      if (props.createMode) {
        handleSubmit();
      } else {
        if (tagRef) {
          tagRef.current?.blur();
        }
      }
    }

    if (key === 'escape') {
      if (props.createMode) {
        props.onCancelDraft();
      } else {
        setTagEditorState({ ...editorState, name: props.tag.name || '' });
        if (tagRef) {
          tagRef.current?.blur();
        }
      }
    }
  };

  const handleAddTagDraft = () => {
    // Other default props are set in action creator
    if (props.tag.id) {
      props.onInitializeTagDraft({
        parent_id: props.tag.id,
      });
    }
  };

  const handleEmojiChange = (emoji: { imageUrl: string }) => {
    setTagEditorState({ ...editorState, emoji: emoji.imageUrl });
    // if we change the emoji and we're editing a tag that already exists, auto save
    if (props.tag.id) {
      const payload: Tag | TagDraft = {
        ...props.tag,
        emoji: emoji.imageUrl,
      };
      handleSave(payload);
    }
  };

  const handleSave = useCallback(
    (payload: Tag | TagDraft) => {
      props.onSave?.(payload, props.createMode).catch(error => {
        let readableError = errorMessageFactory(error);
        if (readableError.toLowerCase().includes('unprocessable entity')) {
          readableError = 'name already exists';
        }
        setTagEditorState({ ...editorState, error: readableError });
        if (tagRef) {
          tagRef.current?.focus();
        }
      });
    },
    [editorState, props]
  );

  const handleSubmit = useCallback(() => {
    if (props.isSaving) {
      return;
    }
    const hasEmojiChanged = editorState.emoji !== props.tag.emoji;
    const hasNameChanged = editorState.name !== props.tag.name;
    // Only save if we have changes:
    if (hasNameChanged || hasEmojiChanged) {
      const payload: Tag | TagDraft = {
        ...props.tag,
        name: editorState.name,
        emoji: editorState.emoji,
      };
      setTagEditorState({ ...editorState, error: null });

      handleSave(payload);
    }
  }, [editorState, handleSave, props]);

  const renderArchiveButtons = () => {
    const { tag, permissions, xhrStatus } = props;

    if (!tag.id || !permissions.canDelete.tags) {
      return null;
    }

    if (tag.active) {
      return (
        <PopoverConfirm
          confirmLabel="Archive"
          onConfirm={handleArchive}
          prompt={archiveConfirmationText()}
          targetCaret={false}
          confirmButtonPriority="high"
          target={
            <Button
              data-testid="archive_tag_button"
              className={styles.actionButton}
              isLoading={xhrStatus?.isSaving}
              priority="low"
            >
              {!xhrStatus?.isSaving ? <FontAwesome name="archive" /> : null} <span>Archive</span>
            </Button>
          }
        />
      );
    }
    const tagId = tag.id;
    return (
      <Button
        data-testid="restore_tag_button"
        isLoading={xhrStatus?.isSaving}
        onClick={() => props.onToggleArchive(tagId)}
        priority="low"
        className={styles.actionButton}
      >
        {!xhrStatus?.isSaving ? <FontAwesome name="undo" /> : null} <span>Restore</span>
      </Button>
    );
  };

  const rowClass = classNames(styles.row, {
    [styles.isDragging]: isDragging,
  });

  const tagDisabled = tag.active === false;
  const tagEnabled = tag.active === true;

  // Memoized dropdown items
  const dropdownItems = useMemo((): DropdownMenuItem[] => {
    const items: DropdownMenuItem[] = [];
    const tic = tag.tic;
    const toolName = tic?.tool_id ? ucFirst(tic.tool_id) : 'external tool';

    // Delete option with TIC-aware confirmation
    let deletePrompt = 'Are you sure you want to delete this tag?';
    if (tic?.uri) {
      deletePrompt = `Are you sure you want to delete this tag? This will only delete in Timely. The original in ${toolName} will not be affected.`;
    }

    items.push({
      type: 'item',
      key: 'delete',
      label: 'Delete',
      variant: 'danger',
      icon: <FontAwesome name="trash" />,
      confirm: {
        prompt: deletePrompt,
        confirmLabel: 'Delete',
        confirmButtonDestructive: true,
      },
      onSelect: () => setTagActionDetails(tag),
    });

    // Visit in external tool option (for TIC-synced tags)
    if (tic?.external_url) {
      items.push({ type: 'separator' });

      const visitOption: DropdownMenuItem = {
        type: 'item',
        key: 'visitExternal',
        label: `Visit in ${toolName}`,
        onSelect: () => window.open(tic.external_url!, '_blank', 'noopener,noreferrer'),
      };

      if (tic.icon) {
        visitOption.icon = <TicMetaIcon ticMeta={tic} size="small" />;
      } else {
        visitOption.icon = <FontAwesome name="external-link" />;
      }

      items.push(visitOption);
    }

    return items;
  }, [tag, setTagActionDetails]);

  if (isDeleting) {
    return null;
  }

  const input =
    !tag.id || permissions.canUpdate.tags ? (
      <Input
        disabled={tagDisabled}
        readOnly={props.isSaving}
        name="name"
        variant="asText"
        ref={tagRef}
        placeholder={!tag.parent_id ? 'Name of tag list' : 'Name of tag'}
        autoFocus={!tag.id}
        onChange={handleChangeName}
        onBlur={handleInputBlur}
        onKeyDown={handleInputKeyDown}
        value={editorState.name}
        className={!tag.parent_id ? styles.tagListName : undefined}
        autoComplete="off"
      />
    ) : (
      <div className={classNames(styles.readOnlyName, { [styles.tagListName]: !tag.parent_id })}>
        {editorState.name}
      </div>
    );

  const canUpdateTag = !tag.id
    ? permissions.canUpdate.tags
    : permissions.canUpdate.tags && tagEnabled;

  if (tag.parent_id && permissions.canUpdate.tags) {
    drag(drop(dragRef));
  }

  return (
    <div ref={dragRef} className={rowClass} key={tag.id}>
      <div data-testid="tags_inner_row" className={styles.innerRow}>
        {!!tag.parent_id && tagEnabled && permissions.canUpdate.tags && (
          <div className={styles.dragHandle}>
            <FontAwesome name="sort" />
          </div>
        )}
        {!!tag.parent_id && (
          <div className={classNames(styles.cell, styles.emoji)}>
            <EmojiPickerDropdown
              disabled={!canUpdateTag}
              value={editorState.emoji}
              onChange={handleEmojiChange}
            />
          </div>
        )}
        {!!tag.tic?.icon && (
          <TicMetaIcon ticMeta={tag.tic} size="small" style={{ position: 'absolute', left: -24 }} />
        )}
        <div className={styles.cell}>
          {editorState.error ? (
            <div>
              <InputDecorator className={styles.error} faIcon="exclamation-triangle">
                {input}
              </InputDecorator>
              <div className={styles.errorMessage}>{capitalize(editorState.error)}</div>
            </div>
          ) : (
            input
          )}
        </div>
        <div data-testid="tags_editor_actions" className={classNames(styles.cell, styles.actions)}>
          {permissions.canCreate.tags && !tag.parent_id && !!tag.id && (
            <Button
              data-testid="add_tag_button"
              className={styles.actionButton}
              disabled={tagDisabled}
              onClick={handleAddTagDraft}
              priority="low"
            >
              <FontAwesome name="plus" />
              <span>Add tag</span>
            </Button>
          )}
          {renderArchiveButtons()}
          {permissions.canDelete.tags && !!tag.id && (
            <DropdownMenu
              closeOnSelect
              trigger={
                <Button data-testid="three_dots" priority="low">
                  <Ellipsis />
                </Button>
              }
              items={dropdownItems}
            />
          )}
          {!tag.id && (
            <ButtonRow>
              <Button
                disabled={editorState.name.length === 0 || props.isSaving}
                isLoading={props.isSaving}
                priority="xhigh"
                onClick={handleSubmit}
              >
                {tag.parent_id ? 'Add tag' : 'Add tag list'}
              </Button>
              <Button priority="low" onClick={props.onCancelDraft}>
                Cancel
              </Button>
            </ButtonRow>
          )}
        </div>
      </div>
      {children && <div className={styles.row}>{children}</div>}
    </div>
  );
};

export default connect<StateProps, {}, OwnProps, IState>(makeMapStateToProps)(TagsEditorItem);
